import React, { useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

// Mapping des wilayas par région
const REGIONS = {
  'Centre': ['Alger', 'Blida', 'Bouira', 'Boumerdes', 'Médéa', 'Tizi Ouzou', 'Tipaza'],
  'Est': ['Annaba', 'Constantine', 'Sétif', 'Batna', 'Béjaïa', 'Jijel', 'Skikda'],
  'Ouest': ['Oran', 'Tlemcen', 'Mostaganem', 'Sidi Bel Abbès', 'Mascara', 'Aïn Témouchent'],
  'Sud': ['Ghardaïa', 'Ouargla', 'Biskra', 'El Oued', 'Béchar', 'Adrar', 'Tamanrasset']
};

const COLORS = {
  'Centre': 'rgb(59, 130, 246)', // Bleu
  'Est': 'rgb(16, 185, 129)',    // Vert
  'Ouest': 'rgb(245, 158, 11)',  // Orange
  'Sud': 'rgb(239, 68, 68)'      // Rouge
};

const RegionalAnalysis = ({ orders }) => {
  const regionalStats = useMemo(() => {
    // Initialiser les statistiques par région
    const stats = Object.keys(REGIONS).reduce((acc, region) => {
      acc[region] = {
        totalOrders: 0,
        confirmedOrders: 0,
        totalRevenue: 0,
        averageOrderValue: 0
      };
      return acc;
    }, {});

    // Calculer les statistiques
    orders.forEach(order => {
      const wilaya = order.shippingAddress?.wilaya;
      if (!wilaya) return;

      const region = Object.entries(REGIONS).find(([_, wilayas]) => 
        wilayas.includes(wilaya)
      )?.[0];

      if (region) {
        stats[region].totalOrders++;
        if (order.status === 'confirmed') {
          stats[region].confirmedOrders++;
        }
        stats[region].totalRevenue += order.totalPrice || 0;
      }
    });

    // Calculer les moyennes
    Object.keys(stats).forEach(region => {
      if (stats[region].totalOrders > 0) {
        stats[region].averageOrderValue = 
          stats[region].totalRevenue / stats[region].totalOrders;
      }
    });

    return stats;
  }, [orders]);

  // Données pour le graphique des commandes par région
  const ordersByRegion = {
    labels: Object.keys(regionalStats),
    datasets: [
      {
        data: Object.values(regionalStats).map(stat => stat.totalOrders),
        backgroundColor: Object.keys(regionalStats).map(region => COLORS[region]),
        borderColor: Object.keys(regionalStats).map(region => COLORS[region]),
        borderWidth: 1,
      },
    ],
  };

  // Options du graphique
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right',
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const region = context.label;
            const stats = regionalStats[region];
            return [
              `Commandes: ${stats.totalOrders}`,
              `Confirmées: ${stats.confirmedOrders}`,
              `CA: ${stats.totalRevenue.toLocaleString('fr-DZ')} DA`,
              `Panier moyen: ${Math.round(stats.averageOrderValue).toLocaleString('fr-DZ')} DA`
            ];
          }
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Performance par région</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Graphique */}
          <div className="relative" style={{ height: '300px' }}>
            <Pie data={ordersByRegion} options={options} />
          </div>

          {/* Tableau des statistiques */}
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Région
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commandes
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taux conf.
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CA
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(regionalStats).map(([region, stats]) => (
                  <tr key={region}>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {region}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                      {stats.totalOrders}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                      {stats.totalOrders ? 
                        `${Math.round((stats.confirmedOrders / stats.totalOrders) * 100)}%` 
                        : '0%'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                      {stats.totalRevenue.toLocaleString('fr-DZ')} DA
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionalAnalysis;
